import Link from 'next/link';
import { useRouter } from 'next/router';
import { isAfter } from 'date-fns';
import { cloneDeep } from 'es-toolkit';

import { Divider, List, Text } from '@zillow/constellation-10';

import { useGetListingDetails } from '../../../hooks/queries/properties';

import StringConstants from '../../../constants/StringConstants';
import { getListingRoute } from '../../../constants/urls';

const DEMOTED_LISTING_COPY =
  'We found a higher quality version of this listing in your account.' +
  ' Any edits made to this version may not be seen by renters on Zillow, Trulia and HotPads. ';
const compareDates = (first: { dateUpdated: string }, second: { dateUpdated: string }) => {
  const firstDate = new Date(first.dateUpdated);
  const secondDate = new Date(second.dateUpdated);
  const isSecondMoreRecent = isAfter(secondDate, firstDate);

  return isSecondMoreRecent ? 1 : -1;
};

const { FORMATTED_BRAND_NAMES } = StringConstants;

const DuplicateListingDetails = () => {
  const router = useRouter();

  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const isSingleSource = listingDetails?.topDupesDetails?.additionalListingSources.length === 1;
  const manualDupeToShow = listingDetails?.isFeed
    ? listingDetails?.topDupesDetails?.dupes
        .sort(compareDates)
        .find(
          (dupe: { feedName: string }) =>
            dupe.feedName === 'Zillow Rental Manager' || dupe.feedName === FORMATTED_BRAND_NAMES.zillow,
        )
    : null;

  if (listingDetails?.isFeed && !manualDupeToShow) {
    return null;
  }

  // If on manual listing, show list of feed sources
  if (listingDetails?.topDupesDetails?.additionalListingSources?.length !== 0) {
    return (
      <>
        <Text data-test="not-posted">
          {`There ${isSingleSource ? 'is an alternative version' : 'are alternative versions'} of
                    this listing being received from the following source${isSingleSource ? '' : 's'},
                    but ${isSingleSource ? 'that version is' : 'those versions are'} not currently posted:`}
        </Text>
        <List.Root appearance="bullet" css={{ paddingLeft: '35px', listStyle: 'initial' }}>
          {cloneDeep(listingDetails?.topDupesDetails.additionalListingSources ?? [])
            .sort()
            .map((source: string, i: number) => (
              <List.Item key={i} css={{ paddingTop: '250' }}>
                {source}
              </List.Item>
            ))}
        </List.Root>
      </>
    );
  }

  return !listingDetails?.topDupesDetails?.isTopDupe || (listingDetails?.isFeed && manualDupeToShow) ? (
    <div data-testid="DuplicateListingDetails">
      <Text asChild textStyle="heading-sm" css={{ marginBottom: { base: '200', lg: '400' } }}>
        <p>Additional listing sources</p>
      </Text>
      <Text>
        {listingDetails?.topDupesDetails?.isTopDupe ? 'Edit your' : DEMOTED_LISTING_COPY}
        <Link
          target="_blank"
          rel="noopener noreferrer"
          href={getListingRoute({
            listingAlias: listingDetails?.topDupesDetails?.isTopDupe
              ? (manualDupeToShow?.listingAlias as string)
              : (listingDetails?.topDupesDetails?.topDupeAliasEncoded as string),
          })}
        >
          {listingDetails?.topDupesDetails?.isTopDupe
            ? `${StringConstants.FORMATTED_BRAND_NAMES_RENTAL.zillow} listing`
            : 'View higher quality listing'}
        </Link>
      </Text>
      <Divider css={{ opacity: 'disabled', flexBasis: 'initial', marginBlock: '600' }} />
    </div>
  ) : null;
};

export default DuplicateListingDetails;
